import styled from '@emotion/styled'
import { useEffect, useState } from 'react'
import { Subtitles, fetchSubtitles } from './Subtitles'
import { PlayIcon, PauseIcon, VolumeOffIcon, VolumeOnIcon } from '../Icons'
import { useIsMobile, BREAKPOINT_MOBILE } from '../../../util/breakpoints'
import { VIEWPORT_HEIGHT_DELTA } from '../../../util/viewportHeight'

interface ControlOptions {
  playPause: boolean
  progress: boolean
  mute: boolean
}

interface MediaControlProps {
  playing: boolean
  progress: number
  muted: boolean
  onPlayPause: () => void
  onProgressClick: (progress: number) => void
  onMuteToggle: () => void
  controls: ControlOptions
  position: 'absolute' | 'fixed'
  mediaURL: string
  subtitleLang: string
  duration: number
  scrollPercentage: number | null
}

export const MediaControl = (props: MediaControlProps) => {
  const [subtitles, setSubtitles] = useState<string>()
  const isMobile = useIsMobile()

  useEffect(() => {
    if (props.mediaURL && props.subtitleLang) {
      fetchSubtitles(props.mediaURL, props.subtitleLang)
        .then((subtitles) => {
          if (subtitles && subtitles.length > 0) {
            setSubtitles(subtitles)
          }
        })
        .catch((error: unknown) => {
          console.error(
            `Failed to fetch subtitles. Video: '${props.mediaURL}', lang '${error}'`
          )
        })
    }
  }, [props.mediaURL, props.subtitleLang])

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const progressBarRect = event.currentTarget.getBoundingClientRect()
    const clickPositionX = event.clientX - progressBarRect.left
    const progressBarWidth = progressBarRect.width
    const clickRatio = Math.min(
      Math.max(clickPositionX / progressBarWidth, 0),
      1
    )
    props.onProgressClick(clickRatio * 100)
  }

  const renderProgressBar = () => (
    <ProgressBarContainer
      controls={props.controls}
      isMobile={isMobile}
      scrollIndicator={props.scrollPercentage !== null}
      onClick={handleProgressClick}
    >
      <ProgressBar value={props.progress} max={100} />
      <ProgressKnob style={{ left: `calc(${props.progress}%)` }} />
      {props.scrollPercentage !== null && (
        <ScrollIndicator scrollPercentage={props.scrollPercentage} />
      )}
    </ProgressBarContainer>
  )

  const renderPlayPauseButton = (size: 'big' | 'small') => (
    <ControlButton size={size} onClick={props.onPlayPause}>
      {!props.playing && (
        <PlayIcon
          width={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
          height={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
        />
      )}
      {props.playing && (
        <PauseIcon
          width={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
          height={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
        />
      )}
    </ControlButton>
  )

  const renderMuteButton = (size: 'big' | 'small') => (
    <ControlButton size={size} onClick={props.onMuteToggle}>
      {props.muted && (
        <VolumeOffIcon
          width={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
          height={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
        />
      )}
      {!props.muted && (
        <VolumeOnIcon
          width={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
          height={`${size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}`}
        />
      )}
    </ControlButton>
  )

  return (
    <MediaControlPanel position={props.position}>
      {subtitles && props.duration > 0 && (
        <SubtitleContainer>
          <Subtitles
            subtitles={subtitles}
            progress={props.progress}
            duration={props.duration}
          />
        </SubtitleContainer>
      )}

      {isMobile ? (
        <MobileControls>
          {props.controls.progress && renderProgressBar()}
          <MobileButtonsContainer controls={props.controls}>
            {props.controls.playPause && renderPlayPauseButton('big')}
            {props.controls.mute && renderMuteButton('small')}
          </MobileButtonsContainer>
        </MobileControls>
      ) : (
        <DesktopControls controls={props.controls}>
          {props.controls.playPause && renderPlayPauseButton('small')}
          {props.controls.progress && renderProgressBar()}
          {props.controls.mute && renderMuteButton('small')}
        </DesktopControls>
      )}
    </MediaControlPanel>
  )
}

const LEFT_RIGHT_PADDING = 30
const PROGRESS_BAR_HEIGHT = 40
const PROGRESS_BAR_HEIGHT_MOBILE = 50
const PROGRESS_BAR_TRACK_HEIGHT = 6
const PROGRESS_KNOB_SIZE = 20
const BIG_BUTTON_SIZE = 53
const SMALL_BUTTON_SIZE = 40

const MediaControlPanel = styled.div<{ position: 'absolute' | 'fixed' }>`
  position: ${(props) => props.position};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  bottom: 20px;
  width: 100%;
  transition: padding 0.2s ease-in-out;
  padding: 0 ${LEFT_RIGHT_PADDING}px
    calc(${VIEWPORT_HEIGHT_DELTA} + env(safe-area-inset-bottom, 0px))
    ${LEFT_RIGHT_PADDING}px;
  pointer-events: none;
  justify-content: flex-end;

  ${(props) => props.theme.mediaControlsContainerCss}
`

const DesktopControls = styled.div<{ controls: ControlOptions }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) =>
    props.controls.playPause && props.controls.progress && props.controls.mute
      ? 'flex-start'
      : 'flex-end'};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`

const MobileControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`

export const ProgressBarContainer = styled.div<{
  controls: ControlOptions
  isMobile: boolean
  scrollIndicator: boolean
}>`
  position: relative;
  width: 100%;
  height: ${(props) =>
    props.isMobile ? PROGRESS_BAR_HEIGHT_MOBILE : PROGRESS_BAR_HEIGHT}px;
  ${(props) => {
    if (props.controls.mute) {
      if (props.scrollIndicator) {
        return 'margin: 0 40px;'
      } else {
        return 'margin: 0 20px;'
      }
    } else {
      return 'margin: 0 60px 0 20px;'
    }
  }}
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media ${BREAKPOINT_MOBILE} {
    margin: 0;
  }
`

export const ProgressBar = styled.progress`
  position: absolute;
  width: 100%;
  height: ${PROGRESS_BAR_TRACK_HEIGHT}px;
  appearance: none;
  background-color: rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 0.15em;
  ${(props) => props.theme.mediaControlsProgressBarUnfilled}

  &::-webkit-progress-bar {
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 0.15em;
    ${(props) => props.theme.mediaControlsProgressBarUnfilled}
  }
  &::-webkit-progress-value {
    border-radius: 0.15em;
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.25);
    ${(props) => props.theme.mediaControlsProgressBarFilled}
  }
  &::-moz-progress-bar {
    border-radius: 0.15em;
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.25);
    ${(props) => props.theme.mediaControlsProgressBarUnfilled}
  }
`

export const ProgressKnob = styled.div`
  position: absolute;
  width: ${PROGRESS_KNOB_SIZE}px;
  height: ${PROGRESS_KNOB_SIZE}px;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 50%;
  transform: translateX(-50%);

  ${(props) => props.theme.mediaControlsProgressBarKnob}
`

export const ScrollIndicator = styled.div<{ scrollPercentage: number }>`
  position: absolute;
  width: 60px;
  border-radius: 10px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.1);
  border: 1px solid white;
  pointer-events: none;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: -1;
  left: calc(
    ${(props) => props.scrollPercentage * 100}% - 30px -
      ${(props) => props.scrollPercentage}px
  );

  @media ${BREAKPOINT_MOBILE} {
    width: 30px;
    border-radius: 5px;
    left: calc(
      ${(props) => props.scrollPercentage * 100}% - 15px -
        ${(props) => props.scrollPercentage}px
    );
  }

  ${(props) => props.theme.mediaControlsScrollIndicator}
`

const ControlButton = styled.div<{ size: 'big' | 'small' }>`
  width: ${(props) =>
    props.size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}px;
  height: ${(props) =>
    props.size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}px;
  min-width: ${(props) =>
    props.size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}px;
  min-height: ${(props) =>
    props.size === 'big' ? BIG_BUTTON_SIZE : SMALL_BUTTON_SIZE}px;
  background-color: #80808066;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 50%;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(props) => props.theme.mediaControlsControlButton}
`

const MobileButtonsContainer = styled.div<{ controls: ControlOptions }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 53px;
  ${(props) =>
    props.controls.mute && !props.controls.playPause
      ? 'padding: 20px 0;'
      : 'padding: 0;'}

  ${(props) =>
    props.controls.mute &&
    `
      ${ControlButton}:last-child {
        position: absolute;
        right: 0px;
      }
    `}
`

const SubtitleContainer = styled.div`
  position: absolute;
  bottom: 80px;
  left: ${LEFT_RIGHT_PADDING}px;
  right: ${LEFT_RIGHT_PADDING}px;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding-bottom: calc(
    ${VIEWPORT_HEIGHT_DELTA} + env(safe-area-inset-bottom, 0px)
  );

  @media ${BREAKPOINT_MOBILE} {
    bottom: 140px;
    padding: 0;
    padding-bottom: calc(
      ${VIEWPORT_HEIGHT_DELTA} + env(safe-area-inset-bottom, 0px)
    );
  }
`
